import { useMemo } from "react";
import { formatAmount } from "@/utils/formatAmount";
import { countedTotalStatuses } from "../api/officialFees";
import { useOfficialFeeMonthlyTotals } from "../hooks/useOfficialFees";
import { monthLabel, newestFirst, totalOfMonths } from "../utils/monthlyTotals";

interface OfficialFeeMonthlyTotalsProps {
  /**
   * The list's own filters. The sums have to describe the rows the person is looking
   * at — a total computed over a wider set than the visible list is a number nobody
   * can check against the table above it.
   */
  filters: Record<string, string | undefined>;
  /** Zaznaczone statusy z filtra listy (pusto = bez zawężenia). */
  statuses?: string[];
}

/**
 * Monthly spend on official fees, under the list — for the technical director.
 *
 * Summed IN THE DATABASE over the whole permission-scoped set, not over the rows the
 * "Załaduj więcej" button happens to have loaded: the list is paginated, so a total
 * added up in the browser would grow as the person scrolls.
 */
export function OfficialFeeMonthlyTotals({ filters, statuses }: OfficialFeeMonthlyTotalsProps) {
  // Wybór wyłącznie statusów spoza sum (np. same „Odrzucone") nie ma czego policzyć —
  // wtedy nie pytamy serwera, zamiast pokazać sumę szerszą niż filtrowana lista.
  const counted = countedTotalStatuses(statuses);
  const { data: rows, isLoading, isError } = useOfficialFeeMonthlyTotals(
    filters,
    statuses,
    counted.length > 0,
  );
  const months = useMemo(() => newestFirst(rows ?? []), [rows]);
  const total = useMemo(() => totalOfMonths(rows ?? []), [rows]);
  const isNarrowed = Object.values(filters).some(Boolean) || !!statuses?.length;

  if (isLoading) {
    return (
      <p className="mt-8 text-sm text-[var(--color-text-muted)]">Liczę sumy miesięczne…</p>
    );
  }
  if (isError) {
    return (
      <p className="mt-8 text-sm text-red-600">Nie udało się policzyć sum miesięcznych.</p>
    );
  }
  if (months.length === 0) return null;

  return (
    <section className="mt-8">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
        Sumy miesięczne
      </h2>
      <p className="mt-1 mb-3 text-xs text-[var(--color-text-muted)]">
        Kwoty wg daty z kolumny „Data”, bez opłat odrzuconych i wycofanych.
        {isNarrowed && " Liczone dla włączonych filtrów listy."}
      </p>
      <div className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] max-w-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Miesiąc</th>
              <th className="text-right px-4 py-2 font-medium">Liczba opłat</th>
              <th className="text-right px-4 py-2 font-medium">Suma</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {months.map((month) => (
              <tr key={month.period}>
                <td className="px-4 py-2 text-[var(--color-text-primary)]">
                  {monthLabel(month.period)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-[var(--color-text-secondary)]">
                  {month.count}
                </td>
                <td className="px-4 py-2 text-right tabular-nums font-medium text-[var(--color-text-primary)]">
                  {formatAmount(month.amount)} zł
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-[var(--color-border-strong)] bg-[var(--color-bg-elevated)]">
            <tr>
              <td className="px-4 py-2 font-semibold text-[var(--color-text-primary)]">Razem</td>
              <td className="px-4 py-2 text-right tabular-nums font-semibold text-[var(--color-text-primary)]">
                {total.count}
              </td>
              <td className="px-4 py-2 text-right tabular-nums font-semibold text-[var(--color-text-primary)]">
                {formatAmount(total.amount)} zł
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
}
